import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { lock } from "src/store/Authentication/authActions";
import { USER_TIMEOUT } from "src/constants/localStorage";

/**
 * Hook personalizado para bloquear la sesión por inactividad del usuario.
 * En lugar de hacer logout y redirigir al login, muestra el lock screen.
 * @param isAuthenticated - Indica si el usuario está autenticado
 */
const useInactivityLogout = (isAuthenticated: boolean) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const performLock = useCallback(() => {
    dispatch(lock());
  }, [dispatch]);

  const resetTimer = useCallback((timeoutSeconds: number) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      performLock();
    }, timeoutSeconds * 1000);
  }, [performLock]);

  // Función para obtener el timeout desde localStorage
  // Retorna null si no está configurado o si el valor es 0
  const getTimeoutFromStorage = useCallback((): number | null => {
    const storedTimeout = localStorage.getItem(USER_TIMEOUT);
    if (storedTimeout) {
      const timeoutSeconds = parseInt(storedTimeout, 10);
      if (!isNaN(timeoutSeconds) && timeoutSeconds > 0) {
        return timeoutSeconds;
      }
    }
    // Si no existe o es 0, retornar null para desactivar el logout automático
    return null;
  }, []);

  const handleActivity = useCallback(() => {
    const timeoutSeconds = getTimeoutFromStorage();
    if (timeoutSeconds !== null && timeoutSeconds > 0) {
      resetTimer(timeoutSeconds);
    }
  }, [resetTimer, getTimeoutFromStorage]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Si no está autenticado, limpiar timers y listeners
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Obtener el valor de USER_TIMEOUT desde localStorage (ya fue obtenido al hacer login)
    const timeoutSeconds = getTimeoutFromStorage();
    
    // Solo activar el timer y los listeners si hay un timeout configurado y es mayor a 0
    if (timeoutSeconds === null || timeoutSeconds <= 0) {
      // Si no hay timeout configurado o es 0, no hacer nada (no activar logout automático)
      return;
    }

    resetTimer(timeoutSeconds);

    // Eventos que indican actividad del usuario
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Agregar listeners para eventos de actividad
    events.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    // Cleanup: remover listeners y limpiar timer cuando el componente se desmonte o el usuario se desautentique
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, handleActivity, resetTimer, getTimeoutFromStorage]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
};

export default useInactivityLogout;

